import React, { useState } from 'react';
import CloseButton from './CloseButton';
import { SpiceLevel } from '../types/menu';
import SpiceSelector from './SpiceSelector';
import { useTheme } from '../context/ThemeContext';
import { CURRENCY } from '../config/currency';

// Item style helper for modernDashboard theme - warm muted colors (not gray)
const getItemStyle = (itemName: string) => {
  const name = itemName.toLowerCase();
  const styles: Record<string, { emoji: string; color: string; gradient: string }> = {
    // Beverages - soft muted
    coffee: { emoji: '☕', color: '#A78B64', gradient: 'linear-gradient(135deg, #FEF7ED 0%, #FEF3C7 100%)' },
    tea: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    chai: { emoji: '🍵', color: '#5B8A72', gradient: 'linear-gradient(135deg, #ECFDF5 0%, #D1FAE5 100%)' },
    juice: { emoji: '🧃', color: '#C2410C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    smoothie: { emoji: '🥤', color: '#BE185D', gradient: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
    water: { emoji: '💧', color: '#0369A1', gradient: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)' },
    soda: { emoji: '🥤', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    cola: { emoji: '🥤', color: '#92400E', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    beer: { emoji: '🍺', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    wine: { emoji: '🍷', color: '#991B1B', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    // Fast food - warm muted
    burger: { emoji: '🍔', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    pizza: { emoji: '🍕', color: '#DC2626', gradient: 'linear-gradient(135deg, #FEE2E2 0%, #FECACA 100%)' },
    pasta: { emoji: '🍝', color: '#EA580C', gradient: 'linear-gradient(135deg, #FFEDD5 0%, #FED7AA 100%)' },
    noodle: { emoji: '🍜', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    // Rice & Indian - warm muted
    rice: { emoji: '🍚', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    biryani: { emoji: '🍛', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    curry: { emoji: '🍛', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    chicken: { emoji: '🍗', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    fish: { emoji: '🐟', color: '#0369A1', gradient: 'linear-gradient(135deg, #E0F2FE 0%, #BAE6FD 100%)' },
    salad: { emoji: '🥗', color: '#15803D', gradient: 'linear-gradient(135deg, #DCFCE7 0%, #BBF7D0 100%)' },
    soup: { emoji: '🍲', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    sandwich: { emoji: '🥪', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    wrap: { emoji: '🌯', color: '#65A30D', gradient: 'linear-gradient(135deg, #ECFCCB 0%, #D9F99D 100%)' },
    fries: { emoji: '🍟', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    // Desserts - warm muted
    cake: { emoji: '🍰', color: '#BE185D', gradient: 'linear-gradient(135deg, #FCE7F3 0%, #FBCFE8 100%)' },
    ice: { emoji: '🍨', color: '#0891B2', gradient: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)' },
    dessert: { emoji: '🍮', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    // Indian breads - warm muted
    dosa: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    idli: { emoji: '⚪', color: '#78716C', gradient: 'linear-gradient(135deg, #F5F5F4 0%, #E7E5E4 100%)' },
    paratha: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    roti: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    naan: { emoji: '🫓', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    // Other - warm muted
    egg: { emoji: '🍳', color: '#A16207', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    paneer: { emoji: '🧀', color: '#CA8A04', gradient: 'linear-gradient(135deg, #FEF9C3 0%, #FEF08A 100%)' },
    dal: { emoji: '🥘', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
    drink: { emoji: '🥤', color: '#0891B2', gradient: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)' },
    beverage: { emoji: '🥤', color: '#0891B2', gradient: 'linear-gradient(135deg, #CFFAFE 0%, #A5F3FC 100%)' },
    meal: { emoji: '🍽️', color: '#6366F1', gradient: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' },
    combo: { emoji: '🍱', color: '#7C3AED', gradient: 'linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%)' },
    thali: { emoji: '🍱', color: '#B45309', gradient: 'linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%)' },
  };
  for (const [keyword, style] of Object.entries(styles)) {
    if (name.includes(keyword)) return style;
  }
  return { emoji: '🍽️', color: '#6366F1', gradient: 'linear-gradient(135deg, #E0E7FF 0%, #C7D2FE 100%)' };
};

interface Portion {
  id: number;
  portionName: string;
  effectivePrice: number;
}

interface StandalonePortionSelectorProps {
  item: {
    id: number;
    name: string;
    portions?: Portion[];
    allowsSpiceCustomization?: boolean;
    spiceLevel?: SpiceLevel;
  };
  existingQuantities?: { [portionId: number]: number }; // Existing quantities from cart
  onClose: () => void;
  onAddToCart: (selections: Array<{ portionId?: number; portionName?: string; quantity: number; price: number; spiceLevel?: SpiceLevel }>) => void;
}

const StandalonePortionSelector: React.FC<StandalonePortionSelectorProps> = ({
  item,
  existingQuantities = {},
  onClose,
  onAddToCart
}) => {
  const { currentThemeName } = useTheme();
  const isModernTheme = currentThemeName === 'modernDashboard';
  const itemStyle = getItemStyle(item.name);
  // Initialize quantities from existing cart or 0
  const [quantities, setQuantities] = useState<{ [portionId: number]: number }>(() => {
    const initial: { [portionId: number]: number } = {};
    item.portions?.forEach(portion => {
      initial[portion.id] = existingQuantities[portion.id] || 0;
    });
    return initial;
  });

  const [selectedSpiceLevel, setSelectedSpiceLevel] = useState<SpiceLevel | null>(
    item.spiceLevel || null
  );

  const handleQuantityChange = (portionId: number, delta: number) => {
    setQuantities(prev => ({
      ...prev,
      [portionId]: Math.max(0, (prev[portionId] || 0) + delta)
    }));
  };

  const getTotalQuantity = () => {
    return Object.values(quantities).reduce((sum, qty) => sum + qty, 0);
  };

  const getTotalAmount = () => {
    let total = 0;
    item.portions?.forEach(portion => {
      const qty = quantities[portion.id] || 0;
      total += qty * portion.effectivePrice;
    });
    return total;
  };

  const handleAddToCart = () => {
    const selections: Array<{ portionId?: number; portionName?: string; quantity: number; price: number; spiceLevel?: SpiceLevel }> = [];
    
    item.portions?.forEach(portion => {
      const qty = quantities[portion.id] || 0;
      if (qty > 0) {
        selections.push({
          portionId: portion.id,
          portionName: portion.portionName,
          quantity: qty,
          price: portion.effectivePrice,
          spiceLevel: item.allowsSpiceCustomization ? selectedSpiceLevel || undefined : undefined
        });
      }
    });

    if (selections.length > 0) {
      onAddToCart(selections);
    } else {
      // If no selections, just close the modal
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-[100001]"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[100002] bg-white shadow-2xl rounded-xl border p-4 w-[90vw] max-w-md max-h-[85vh] overflow-y-auto"
        style={isModernTheme ? {
          borderColor: `${itemStyle.color}30`,
          borderWidth: '2px'
        } : {
          borderColor: 'var(--border-subtle)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="flex justify-between items-center mb-4 pb-3 border-b"
          style={{ borderBottomColor: isModernTheme ? `${itemStyle.color}20` : '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            {isModernTheme && (
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: itemStyle.gradient }}
              >
                <span className="text-2xl">{itemStyle.emoji}</span>
              </div>
            )}
            <div>
              <h3 
                className="font-bold text-lg"
                style={{ color: isModernTheme ? itemStyle.color : 'var(--text-primary)' }}
              >
                {item.name}
              </h3>
              {isModernTheme && (
                <p className="text-xs text-gray-500">Select portion sizes</p>
              )}
            </div>
          </div>
          <CloseButton onClick={onClose} size="lg" />
        </div>

        {/* Portions List */}
        <div className="space-y-3 mb-4">
          {item.portions?.map((portion, idx) => {
            const qty = quantities[portion.id] || 0;
            const isSelected = qty > 0;
            return (
              <div 
                key={portion.id}
                className="flex items-center justify-between gap-3 py-3 px-3 rounded-xl transition-all"
                style={isModernTheme ? {
                  backgroundColor: isSelected ? `${itemStyle.color}10` : '#F9FAFB',
                  border: isSelected ? `2px solid ${itemStyle.color}40` : '2px solid transparent'
                } : {
                  backgroundColor: 'var(--bg-sunken)'
                }}
              >
                {/* Portion Name - Left */}
                <div className="flex-1 min-w-0">
                  <div 
                    className="font-semibold text-base truncate"
                    style={{ color: isModernTheme && isSelected ? itemStyle.color : 'var(--text-primary)' }}
                  >
                    {portion.portionName}
                  </div>
                </div>
                
                {/* Quantity Controls - Center */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleQuantityChange(portion.id, -1)}
                    disabled={qty === 0}
                    className="w-9 h-9 flex items-center justify-center rounded-full transition disabled:opacity-30 disabled:cursor-not-allowed"
                    style={isModernTheme ? {
                      background: qty === 0 ? '#e5e7eb' : `${itemStyle.color}20`,
                      color: qty === 0 ? '#9ca3af' : itemStyle.color,
                      border: 'none'
                    } : {
                      background: 'var(--bg-sunken)',
                      color: 'var(--text-secondary)',
                      border: 'none'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20 12H4" />
                    </svg>
                  </button>
                  <span 
                    className="w-8 text-center font-bold text-lg"
                    style={{ color: isModernTheme ? itemStyle.color : 'var(--text-primary)' }}
                  >
                    {qty}
                  </span>
                  <button
                    onClick={() => handleQuantityChange(portion.id, 1)}
                    className="w-9 h-9 flex items-center justify-center rounded-full transition"
                    style={isModernTheme ? {
                      background: itemStyle.color,
                      color: 'white',
                      border: 'none'
                    } : {
                      background: '#1f2937', /* basil-todo */
                      color: 'white',
                      border: 'none'
                    }}
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
                    </svg>
                  </button>
                </div>

                {/* Price - Right */}
                <div 
                  className="font-bold text-base whitespace-nowrap"
                  style={{ color: isModernTheme ? itemStyle.color : 'var(--text-primary)' }}
                >
                  {CURRENCY.symbol}{portion.effectivePrice.toFixed(2)}
                </div>
              </div>
            );
          })}
        </div>

        {/* Spice Level Selector */}
        {item.allowsSpiceCustomization && (
          <div 
            className="mb-4 pb-4 border-b"
            style={{ borderBottomColor: isModernTheme ? `${itemStyle.color}20` : '#e5e7eb' }}
          >
            <SpiceSelector
              selectedLevel={selectedSpiceLevel}
              onChange={setSelectedSpiceLevel}
              size="small"
            />
          </div>
        )}

        {/* Footer */}
        <div 
          className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 sm:gap-0 pt-3 border-t"
          style={{ borderTopColor: isModernTheme ? `${itemStyle.color}20` : '#e5e7eb' }}
        >
          <div className="flex items-center justify-between sm:block">
            <span className="text-sm text-gray-500">Total</span>
            <span 
              className="text-xl font-bold sm:block"
              style={{ color: isModernTheme ? itemStyle.color : 'var(--text-primary)' }}
            >
              {CURRENCY.symbol}{getTotalAmount().toFixed(2)}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 text-gray-700 font-semibold transition text-sm sm:text-base"
              style={isModernTheme ? {
                backgroundColor: 'var(--bg-sunken)',
                borderRadius: '9999px',
                border: '1px solid var(--border-subtle)'
              } : {
                backgroundColor: 'var(--bg-sunken)',
                borderRadius: '8px'
              }}
            >
              Cancel
            </button>
            <button
              onClick={handleAddToCart}
              disabled={getTotalQuantity() === 0}
              className="flex-1 sm:flex-none px-4 sm:px-6 py-2.5 text-white font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
              style={isModernTheme ? {
                backgroundColor: itemStyle.color,
                borderRadius: '9999px',
                boxShadow: `0 4px 12px ${itemStyle.color}40`
              } : {
                backgroundColor: 'var(--action)',
                borderRadius: '8px'
              }}
            >
              Add to Cart
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default StandalonePortionSelector;

